import { Controller } from 'stimulus';

/**
 * Counts RPM unit values from all VIP rows and displays totals.
 *
 * Listens for turbo:before-stream-render events and recalculates totals
 * after streams finish loading. Uses debouncing to batch updates.
 *
 * Expected HTML structure:
 * - Unit count cells: data-rpm-unit-totals-target="unitCell" data-rpm-code="<code>"
 * - Total cells: data-rpm-unit-totals-target="totalCell" data-rpm-code="<code>"
 */
export default class extends Controller {
  static targets = ['unitCell', 'totalCell'];

  static values = {
    codes: Array, // List of RPM codes to track, e.g. ["99453", "99454", "99458"]
    debounceMs: { type: Number, default: 100 },
  };

  connect() {
    this.pendingUpdate = null;

    // Listen for turbo stream renders
    document.addEventListener(
      'turbo:before-stream-render',
      this.scheduleUpdate
    );

    // Also listen for turbo:load in case of full page loads
    document.addEventListener('turbo:load', this.scheduleUpdate);

    // Initial calculation
    this.scheduleUpdate();
  }

  disconnect() {
    document.removeEventListener(
      'turbo:before-stream-render',
      this.scheduleUpdate
    );
    document.removeEventListener('turbo:load', this.scheduleUpdate);

    if (this.pendingUpdate) {
      clearTimeout(this.pendingUpdate);
    }
  }

  scheduleUpdate = () => {
    // Debounce to wait for all streams to finish
    if (this.pendingUpdate) {
      clearTimeout(this.pendingUpdate);
    }

    this.pendingUpdate = setTimeout(() => {
      this.updateTotals();
      this.pendingUpdate = null;
    }, this.debounceMsValue);
  };

  updateTotals() {
    this.codesValue.forEach((code) => {
      const total = this.calculateTotalForCode(code);
      this.updateTotalDisplay(code, total);
    });
  }

  calculateTotalForCode(code) {
    let total = 0;

    this.unitCellTargets
      .filter((cell) => cell.dataset.rpmCode === code)
      .forEach((cell) => {
        const value = parseInt(cell.textContent.trim(), 10);
        if (!isNaN(value)) {
          total += value;
        }
      });

    return total;
  }

  updateTotalDisplay(code, total) {
    const totalCell = this.totalCellTargets.find(
      (cell) => cell.dataset.rpmCode === code
    );

    if (totalCell) {
      totalCell.textContent = total;
    }
  }
}
